import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteImages } from "@/context/SiteImagesContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { getIcon } from "@/lib/icons";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const BENEFITS = [
  { iconKey: "artben1_icon", titleKey: "artben1_title", descKey: "artben1_desc" },
  { iconKey: "artben2_icon", titleKey: "artben2_title", descKey: "artben2_desc" },
  { iconKey: "artben3_icon", titleKey: "artben3_title", descKey: "artben3_desc" },
];

const TEMPLATES = [
  { titleKey: "tpl1_title", descKey: "tpl1_desc", imgKey: "art1" },
  { titleKey: "tpl2_title", descKey: "tpl2_desc", imgKey: "art2" },
  { titleKey: "tpl3_title", descKey: "tpl3_desc", imgKey: "art3" },
  { titleKey: "tpl4_title", descKey: "tpl4_desc", imgKey: "art4" },
];

export const ArtTemplates = () => {
  const { img } = useSiteImages();
  const { t } = useSiteContent();
  return (
    <section id="templates" className="relative py-28 px-6 bg-gradient-to-b from-transparent via-[#0a0a12] to-transparent" data-testid="templates-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {t('templates_title')} <span className="text-gradient">{t('templates_highlight')}</span>
          </h2>
          <p className="mt-4 text-white/55 text-lg">{t('templates_subtitle')}</p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {BENEFITS.map((b, i) => {
            const Icon = getIcon(t(b.iconKey));
            return (
              <motion.div
                key={b.titleKey}
                data-testid={`art-benefit-${i}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="glass rounded-2xl p-7 text-center"
              >
                <span className="grid place-items-center h-12 w-12 mx-auto rounded-xl bg-white/5 mb-4">
                  <Icon className="h-6 w-6 text-violet-400" />
                </span>
                <h3 className="text-lg font-semibold mb-1.5">{t(b.titleKey)}</h3>
                <p className="text-white/55 text-sm">{t(b.descKey)}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-20 text-center max-w-2xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t('templates_gallery_title')}</h3>
          <p className="mt-3 text-white/55">{t('templates_gallery_subtitle')}</p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TEMPLATES.map((tpl, i) => (
            <motion.div
              key={tpl.titleKey}
              data-testid={`template-card-${i}`}
              initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.08 }}
              className="group rounded-2xl overflow-hidden border border-white/10 relative"
            >
              <div className="aspect-square overflow-hidden">
                <img src={img(tpl.imgKey)} alt={t(tpl.titleKey)} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h4 className="font-semibold text-white">{t(tpl.titleKey)}</h4>
                <p className="text-white/65 text-xs mt-1 leading-relaxed">{t(tpl.descKey)}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Button
            data-testid="view-templates-button"
            onClick={() => scrollTo("contact")}
            variant="outline"
            className="rounded-full h-12 px-8 bg-white/5 border-white/15 text-white hover:bg-white/10 gap-2 text-base"
          >
            {t('templates_cta')} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};
